// The bean library of a machine in the app (concept beans-and-shot-history, variant B decided 19.10.2026): the cloud
// keeps every bean with its names, bags and recipe versions; the machine holds only the current recipe. The list after
// every (re)connect and after every bean delta, the detail of one bean on demand. A bean's data is taken only with a
// cloudRev at least as high as the app knows for it (AD-44), so a late answer never brings back an older state.
import type {
  BagRequest,
  BeanCreateRequest,
  BeanItem,
  BeanListResponse,
  BeanRecipe,
  BeanUpdateMessage,
  BeanUpdateRequest,
} from '@brewcore/app-contract';
import { jsonRequest, type FetchLike, type HttpResult, type HttpTarget } from './http.js';

/** REST of the library (app-v1 http-beans, http-bean). */
export class BeansApi {
  private readonly target: HttpTarget;

  constructor(
    fetchImpl: FetchLike = (input, init) => fetch(input, init),
    base = '',
    headers: Record<string, string> = {},
  ) {
    this.target = { fetchImpl, base, headers };
  }

  list(machine: string): Promise<HttpResult<BeanListResponse>> {
    return jsonRequest(this.target, 'GET', `/api/machines/${machine}/beans`, null, 'http-beans');
  }

  get(machine: string, id: string): Promise<HttpResult<BeanItem>> {
    return jsonRequest(this.target, 'GET', `/api/machines/${machine}/beans/${id}`, null, 'http-bean');
  }

  create(machine: string, body: BeanCreateRequest): Promise<HttpResult<BeanItem>> {
    return jsonRequest(this.target, 'POST', `/api/machines/${machine}/beans`, body, 'http-bean');
  }

  update(machine: string, id: string, body: BeanUpdateRequest): Promise<HttpResult<BeanItem>> {
    return jsonRequest(this.target, 'PATCH', `/api/machines/${machine}/beans/${id}`, body, 'http-bean');
  }

  remove(machine: string, id: string): Promise<HttpResult<null>> {
    return jsonRequest(this.target, 'DELETE', `/api/machines/${machine}/beans/${id}`, null, null);
  }

  addBag(machine: string, id: string, body: BagRequest): Promise<HttpResult<BeanItem>> {
    return jsonRequest(this.target, 'POST', `/api/machines/${machine}/beans/${id}/bags`, body, 'http-bean');
  }

  updateBag(machine: string, id: string, bag: string, body: BagRequest): Promise<HttpResult<BeanItem>> {
    return jsonRequest(
      this.target,
      'PATCH',
      `/api/machines/${machine}/beans/${id}/bags/${bag}`,
      body,
      'http-bean',
    );
  }

  addRecipe(machine: string, id: string, recipe: BeanRecipe): Promise<HttpResult<BeanItem>> {
    return jsonRequest(
      this.target,
      'POST',
      `/api/machines/${machine}/beans/${id}/recipes`,
      { recipe },
      'http-bean',
    );
  }

  /** "Bohne wählen": the API brings the bean onto the machine if needed and chooses it (waits for the machine). */
  setReference(machine: string, id: string, shotId: string | null): Promise<HttpResult<BeanItem>> {
    return jsonRequest(
      this.target,
      'PUT',
      `/api/machines/${machine}/beans/${id}/reference`,
      { shotId },
      'http-bean',
    );
  }

  select(machine: string, id: string): Promise<HttpResult<null>> {
    return jsonRequest(this.target, 'POST', `/api/machines/${machine}/beans/${id}/select`, null, null);
  }
}

export interface BeansView {
  /** the current bean first, then the ones on the machine, then by the newest shot */
  items: readonly BeanItem[];
  loaded: boolean;
  loading: boolean;
  /** fetched details (names, every recipe version, every bag), by bean ID */
  details: Readonly<Record<string, BeanItem>>;
  error: string | null;
}

/** Deltas of several beans in a row (a shot touches its bean) become one reload. */
export const BEANS_RELOAD_MS = 300;

export class BeansStore {
  private view: BeansView = { items: [], loaded: false, loading: false, details: {}, error: null };
  private readonly listeners = new Set<() => void>();
  private readonly revs = new Map<string, number>();
  private refreshing: Promise<void> | null = null;
  private again = false;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    readonly machineId: string,
    private readonly api: BeansApi,
  ) {}

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): BeansView => this.view;

  private commit(patch: Partial<BeansView>): void {
    this.view = { ...this.view, ...patch };
    for (const fn of this.listeners) fn();
  }

  /** The list, after every snapshot and after bean deltas; a call while one runs loads once more after it. */
  refresh(): Promise<void> {
    if (this.refreshing) {
      this.again = true;
      return this.refreshing;
    }
    this.refreshing = this.load().finally(() => {
      this.refreshing = null;
      if (this.again) {
        this.again = false;
        void this.refresh();
      }
    });
    return this.refreshing;
  }

  private async load(): Promise<void> {
    this.commit({ loading: true });
    const r = await this.api.list(this.machineId);
    if (r.ok) {
      const items = r.value.items.map((b) => this.keep(b));
      // details stay only where the list has nothing newer (they are fetched again when opened)
      const details: Record<string, BeanItem> = {};
      for (const [id, d] of Object.entries(this.view.details)) {
        const fresh = items.find((b) => b.id === id);
        if (fresh && fresh.cloudRev <= d.cloudRev) details[id] = d;
      }
      this.commit({ items, details, loaded: true, error: null });
    } else this.commit({ error: r.error.code });
    this.commit({ loading: false });
  }

  /** The known version if it is newer than b (by cloudRev); else b, and its revision is remembered. */
  private keep(b: BeanItem): BeanItem {
    const known = this.revs.get(b.id);
    if (known !== undefined && known > b.cloudRev) return this.view.items.find((x) => x.id === b.id) ?? b;
    this.revs.set(b.id, b.cloudRev);
    return b;
  }

  onUpdate(m: BeanUpdateMessage): void {
    if (m.cloudRev <= (this.revs.get(m.id) ?? -1)) return;
    if (m.deleted) {
      const details = { ...this.view.details };
      delete details[m.id];
      this.revs.set(m.id, m.cloudRev);
      this.commit({ items: this.view.items.filter((b) => b.id !== m.id), details });
      return;
    }
    this.reloadSoon();
  }

  /**
   * The machine reported state/beans: "aktiv" and "auf der Maschine" come from it, not from a bean row (a switch with
   * bean.put changes no row), so the list loads again, together with any bean deltas of the same moment.
   */
  onMachineBeans(): void {
    if (this.view.loaded) this.reloadSoon();
  }

  private reloadSoon(): void {
    if (this.timer) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.refresh();
      for (const id of Object.keys(this.view.details)) void this.detail(id, true);
    }, BEANS_RELOAD_MS);
  }

  /** One bean with its names, recipe versions and bags (cached until a newer revision is known). */
  async detail(id: string, force = false): Promise<BeanItem | null> {
    const cached = this.view.details[id];
    if (cached && !force && cached.cloudRev >= (this.revs.get(id) ?? -1)) return cached;
    const r = await this.api.get(this.machineId, id);
    if (!r.ok) {
      if (!cached) this.commit({ error: r.error.code });
      return cached ?? null;
    }
    if (cached && cached.cloudRev > r.value.cloudRev) return cached;
    this.revs.set(id, Math.max(this.revs.get(id) ?? -1, r.value.cloudRev));
    this.commit({ details: { ...this.view.details, [id]: r.value } });
    return r.value;
  }

  /** A bean the API answered with (after a change): into the list and the details, by cloudRev. */
  private take(b: BeanItem): void {
    if (b.cloudRev < (this.revs.get(b.id) ?? -1)) return;
    this.revs.set(b.id, b.cloudRev);
    const listed: BeanItem = { ...b };
    delete listed.names;
    delete listed.recipes;
    delete listed.bags;
    const items = this.view.items.some((x) => x.id === b.id)
      ? this.view.items.map((x) => (x.id === b.id ? listed : x))
      : [...this.view.items, listed];
    this.commit({ items, details: { ...this.view.details, [b.id]: b } });
  }

  private async change(r: Promise<HttpResult<BeanItem>>): Promise<HttpResult<BeanItem>> {
    const x = await r;
    if (x.ok) this.take(x.value);
    else if (x.error.code === 'rev_conflict') void this.refresh();
    return x;
  }

  create(body: BeanCreateRequest): Promise<HttpResult<BeanItem>> {
    return this.change(this.api.create(this.machineId, body));
  }

  update(id: string, body: BeanUpdateRequest): Promise<HttpResult<BeanItem>> {
    return this.change(this.api.update(this.machineId, id, body));
  }

  addBag(id: string, body: BagRequest): Promise<HttpResult<BeanItem>> {
    return this.change(this.api.addBag(this.machineId, id, body));
  }

  updateBag(id: string, bag: string, body: BagRequest): Promise<HttpResult<BeanItem>> {
    return this.change(this.api.updateBag(this.machineId, id, bag, body));
  }

  async remove(id: string): Promise<HttpResult<null>> {
    const r = await this.api.remove(this.machineId, id);
    if (r.ok) {
      const details = { ...this.view.details };
      delete details[id];
      this.revs.set(id, Number.MAX_SAFE_INTEGER);
      this.commit({ items: this.view.items.filter((b) => b.id !== id), details });
    }
    return r;
  }

  /** Runs before a bean is chosen: the client sends the stepper's collected values first (D-B02). */
  beforeSelect: (() => Promise<void>) | null = null;

  async select(id: string): Promise<HttpResult<null>> {
    await this.beforeSelect?.();
    return this.api.select(this.machineId, id);
  }

  /** The reference of a bean (a shot of it, or none); the machine's copy follows if it is the current bean. */
  setReference(id: string, shotId: string | null): Promise<HttpResult<BeanItem>> {
    return this.change(this.api.setReference(this.machineId, id, shotId));
  }

  /** A bean of the list or its detail, by ID. */
  find(id: string | null | undefined): BeanItem | null {
    if (!id) return null;
    return this.view.details[id] ?? this.view.items.find((b) => b.id === id) ?? null;
  }
}
